import {
  Tag,
  Avatar,
  DATE_FORMAT,
  SkeletonText,
  Table,
} from '@dev-spendesk/grapes';
import { Trans } from 'react-i18next';

import { PanelHeader } from 'common/components/Panel';
import { fallbackSupplierLogoSrc } from 'common/components/SupplierLogo';
import { useTranslation } from 'common/hooks/useTranslation';
import { isInvoiceBill } from 'modules/invoices/models';
import { SupplierBankInfoPreviewContainer } from 'modules/suppliers';
import { useCompanyCurrency } from 'src/core/modules/app/hooks/useCompanyCurrency';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { isInternationalTransfer } from 'src/core/utils';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import { useGetInternationalRecipientByIdQuery } from '../../../hooks';
import { useIsIwtEnabled } from '../../../hooks/api/useIsIwtEnabled';
import { type TransferDetails } from '../../../models';

type Props = {
  transfer: TransferDetails;
};

export const HeaderSection = ({ transfer }: Props) => {
  const { t, localeFormat } = useTranslation('global');
  const {
    amount,
    bill: { counterparty },
  } = transfer;

  const isIwtEnabled = useIsIwtEnabled();

  const companyId = useCompanyId();

  const companyCurrency = useCompanyCurrency();

  const isCrossCurrency = isInternationalTransfer({
    sourceCurrency: companyCurrency,
    targetCurrency: amount.currency,
    targetCountry: counterparty.bankAddress?.country,
  });

  // Fetch international recipient details in case this is a cross-currency transfer.
  // Display these details instead of the default supplier bank info.
  const { internationalRecipientDetails, isLoading } =
    useGetInternationalRecipientByIdQuery({
      id: counterparty.id,
      companyId,
      enabled: isIwtEnabled && isCrossCurrency,
    });

  return (
    <PanelHeader
      top={
        <>
          <h3 className="text-content-primary title-l flex items-center">
            <Avatar
              src={counterparty.logo}
              fallbackSrc={fallbackSupplierLogoSrc}
              size={32}
              text={counterparty.name}
              variant="square"
              className="mr-16"
            />
            <span>{counterparty.name}</span>
          </h3>

          {isLoading ? <SkeletonText className="mt-24" size="xxl" /> : null}

          {!internationalRecipientDetails.length &&
            !isLoading &&
            isInvoiceBill(transfer.bill) && (
              <SupplierBankInfoPreviewContainer
                className="mt-24"
                supplierId={transfer.bill.counterparty.id}
              />
            )}

          {!isLoading &&
          isCrossCurrency &&
          internationalRecipientDetails.length ? (
            <Table.Mini
              className="mt-24"
              columns={[
                {
                  id: 'label',
                  width: '80px',
                  renderCell: ({ label }) => (
                    <div className="truncate">{label}</div>
                  ),
                },
                {
                  id: 'value',
                  renderCell: ({ value }) => value,
                },
              ]}
              data={internationalRecipientDetails}
              getRowId={(row) => String(row.id)}
            />
          ) : null}
        </>
      }
      textPrimary={formatMonetaryValue(transfer.amount)}
      textSecondary={
        <Trans
          i18nKey="invoices.transfer.panel.header.scheduledFor"
          values={{
            date: localeFormat(
              new Date(transfer.scheduledForDate),
              DATE_FORMAT.SHORT,
            ),
          }}
          components={[<strong key="bold" />]}
        />
      }
    >
      <Tag variant="neutral">{t('invoices.transfer.panel.header.tag')}</Tag>
    </PanelHeader>
  );
};
